import { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'src/contexts/StoreContext';
import { useToolOptionsRendererLogic } from '../ToolOptionsRenderer.logic';
import { ToolOptionsRendererProps } from '../../../BaseToolbar.types';
import { ToolOptionsProps } from '../ToolOptionsRenderer.types';
import {
  TextAlignment,
  FONT_SIZES,
  FORMAT_TO_FONT_SIZE,
  CustomFontName,
  FontStyle,
  Formats,
} from 'src/constants/font.constants';
import { getColorI18nKey, getToolOptionTitleI18nKey } from 'src/_shared/utils/getI18nKeys';
import { DYNAMIC_MODAL_GROUPS } from 'src/store/toast/toastStore.types';
import { COLOR_TOOL_PALETTE } from '../color-tool/color-tool-option/ColorToolOption.constants';
import { getColorToolOptionKey } from '../color-tool/color-tool-option/ColorToolOption.utils';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { TestIDs } from 'src/constants/testIDs.constants';
import { removeUnprintableCharacters } from 'src/_shared/utils/removeUnprintableCharacters';

export const useTextToolOptionsLogic = (
  { options }: ToolOptionsRendererProps<ToolOptionsProps>,
  customIsOptionSelectedValidation?: (option: ToolOptionsProps) => boolean,
) => {
  const { scrollViewRef, getScrollViewElementTestId } = useToolOptionsRendererLogic(
    { options },
    customIsOptionSelectedValidation,
  );

  const {
    project: { activeProject, activeGroup },
    i18n,
    toastStore,
  } = useStore();

  const selectedUserTexts = activeGroup?.selectedProducts?.map(p => p.userTexts?.[0])?.filter(Boolean) ?? [];

  const listFormatsSelected = activeGroup?.selectedProducts
    ?.filter(p => p?.userTexts != undefined)
    ?.map(i => i?.actualFormat);

  const getSmallestFontSize = (formats: (string | undefined)[] | undefined): number => {
    const validFormats = formats?.filter((f): f is string => !!f);
    if (!validFormats || validFormats.length === 0) return 14;
    const parsed = validFormats.map(f => {
      const [w, h] = f.split('x').map(Number);
      return { format: f as Formats, width: w, height: h, area: w * h };
    });
    const smallest = parsed.reduce((min, cur) => (cur.area < min.area ? cur : min));
    return FORMAT_TO_FONT_SIZE[smallest.format];
  };

  const maxValueFontSize = getSmallestFontSize(listFormatsSelected);

  const defaultValues = useMemo(
    () => ({
      text: '',
      font: CustomFontName.PlayfairDisplay,
      style: FontStyle.Regular,
      alignment: TextAlignment.Center,
      color: COLOR_TOOL_PALETTE.black,
      fontSize: maxValueFontSize,
    }),
    [maxValueFontSize],
  );

  const objectToInject = useMemo(() => {
    const propsToCompare = ['text', 'font', 'color', 'style', 'size', 'anchor'] as const;

    if (
      selectedUserTexts.length === 0 ||
      !propsToCompare.every(prop => {
        const first = selectedUserTexts[0]?.[prop];
        return selectedUserTexts.every(t => t?.[prop] === first);
      })
    ) {
      return undefined;
    }
    return selectedUserTexts[0];
  }, [selectedUserTexts]);

  const initialValues = useMemo(
    () =>
      objectToInject
        ? {
            ...defaultValues,
            text: objectToInject.text,
            font: objectToInject.font,
            style: objectToInject.style,
            alignment: objectToInject.anchor as TextAlignment,
            color: objectToInject.color,
            fontSize: objectToInject.size,
          }
        : defaultValues,
    [objectToInject, defaultValues],
  );

  const [values, setValues] = useState(initialValues);

  const fontSizes = FONT_SIZES.filter(size => size <= maxValueFontSize);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const update = <K extends keyof typeof values>(key: K, value: (typeof values)[K]) => {
    let cleanValue = value;
    if (key === 'text' && typeof value === 'string') {
      cleanValue = removeUnprintableCharacters(value) as (typeof values)[K];
    }
    setValues(prev => ({ ...prev, [key]: cleanValue }));
  };

  const onChangeFont = (font: string) =>
    setValues(prev => ({ ...prev, font, style: font === prev.font ? prev.style : FontStyle.Regular }));

  const cleanValueText = () => setValues(prev => ({ ...prev, text: prev.text.trim().replaceAll(/\n/g, ' ') }));

  const colorPaletteOptions = useMemo(
    () =>
      Object.values(COLOR_TOOL_PALETTE).map(color => ({
        selected: color === values.color,
        choiceDetails: { color },
        titleKey: getColorI18nKey(getColorToolOptionKey(color)),
        enabled: true,
        productOptionCode: TestIDs.TEXT_COLOR,
      })),
    [values.color],
  );

  const handleConfirmButton = () => {
    if (!activeProject) return;
    const { text, fontSize, font, color, alignment, style } = values;

    activeProject.setProductsUserText(activeGroup?.selectedProducts, text, fontSize, font, color, alignment, style);
    amplitude.trackEvent(AnalyticsEvents.personalize.lookupSelect, {
      lookup_code: 'text',
      lookup_product: activeGroup?.code,
      lookup_id: '',
    });
    toastStore.showToast({
      message: i18n.t('toast:product-option-applied-successfully', {
        option: i18n.t(getToolOptionTitleI18nKey('text')),
      }),
      duration: 3000,
      id: 'apply-product-option-text',
      group: DYNAMIC_MODAL_GROUPS.ApplyProductOption,
    });
  };

  return {
    scrollViewRef,
    getScrollViewElementTestId,
    handleConfirmButton,
    ...values,
    colorPaletteOptions,
    fontSizes,
    onChangeText: (v: string) => update('text', v),
    onChangeFont,
    onChangeStyle: (v: string) => update('style', v),
    onChangeAlignment: (v: TextAlignment) => update('alignment', v),
    onChangeColor: (v: string) => update('color', v),
    cleanValueText,
    onChangeFontSize: (v: number) => update('fontSize', v),
  };
};
